import React, { useState, useEffect } from 'react';
import '../AssessmentForm.css';
import { getTreatmentPathwayOptions } from '../treatmentPathwayOptions';

/**
 * ClinicalCheckTab
 *
 * Handles Clinical Check section for multiple report types
 * Maps to Report.HealthcareDelivery table fields
 *
 * Sections handled:
 * - psych-triage-followup-clinical-check (2003 Triage Follow Up)
 * - psych-eform-initial-clinical-check (2010 Network Eform Initial)
 * - psych-eform-followup-clinical-check (2011 Network Eform Follow Up)
 */

const GRADE_OPTIONS = [
  { value: 'A', label: 'A' },
  { value: 'B', label: 'B' },
  { value: 'C', label: 'C' },
  { value: 'D', label: 'D' }
];

const ClinicalCheckTab = ({ reportId, api, onSave, sectionName, isPublished, patientInfo }) => {
  // Form state - common fields
  const [grade, setGrade] = useState('');
  const [gradeComments, setGradeComments] = useState('');
  const [auditOption, setAuditOption] = useState('');

  // Form state - section-specific fields
  const [reportObjectiveCriteria, setReportObjectiveCriteria] = useState([]);
  const [treatmentPathwayID, setTreatmentPathwayID] = useState('');

  // UI state
  const [loading, setLoading] = useState(true);
  const [savingStates, setSavingStates] = useState({});
  const [saveTimeouts, setSaveTimeouts] = useState({});
  const [initialCharCounts, setInitialCharCounts] = useState({});
  const [objectiveCriteriaOptions, setObjectiveCriteriaOptions] = useState([]);
  const [auditOptions, setAuditOptions] = useState([]);
  const treatmentPathwayOptions = getTreatmentPathwayOptions(patientInfo?.isCYPApplicable);

  // Determine section type
  const isTriageFollowUp = sectionName === 'psych-triage-followup-clinical-check';
  const isEformInitial = sectionName === 'psych-eform-initial-clinical-check';
  const isEformFollowUp = sectionName === 'psych-eform-followup-clinical-check';

  useEffect(() => {
    loadClinicalCheckData();
  }, [reportId, sectionName]);

  const loadClinicalCheckData = async () => {
    try {
      setLoading(true);
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/${sectionName}`);

      if (response.ok) {
        const data = await response.json();

        // Common fields
        setGrade(data.grade || '');
        setGradeComments(data.gradeComments || '');
        setAuditOption(data.auditOption !== null ? data.auditOption : '');

        // Section-specific fields
        if (isTriageFollowUp) {
          setReportObjectiveCriteria(data.reportObjectiveCriteria || []);
        }
        if (isEformFollowUp) {
          setTreatmentPathwayID(data.treatmentPathwayID !== null ? data.treatmentPathwayID : '');
        }

        // Load dropdown options (treatmentPathwayOptions now hardcoded)
        if (data.objectiveCriteriaOptions) {
          setObjectiveCriteriaOptions(data.objectiveCriteriaOptions);
        }
        if (data.auditOptions) {
          setAuditOptions(data.auditOptions);
        }

        // Set loaded fields as 'saved'
        const initialSavingStates = {};
        if (data.grade) initialSavingStates['Grade'] = 'saved';
        if (data.gradeComments) initialSavingStates['GradeComments'] = 'saved';
        if (data.auditOption !== null) initialSavingStates['AuditOption'] = 'saved';
        if (isTriageFollowUp && data.reportObjectiveCriteria?.length > 0) {
          initialSavingStates['ReportObjectiveCriteria'] = 'saved';
        }
        if (isEformFollowUp && data.treatmentPathwayID !== null) {
          initialSavingStates['TreatmentPathwayID'] = 'saved';
        }
        setSavingStates(initialSavingStates);
      }
    } catch (error) {
      console.error('Error loading clinical check data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveField = async (fieldName, value) => {
    if (isPublished) return;
    setSavingStates(prev => ({ ...prev, [fieldName]: 'saving' }));

    try {
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/${sectionName}/fields/${fieldName}`, {
        method: 'PATCH',
        body: JSON.stringify({ value })
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      setSavingStates(prev => ({ ...prev, [fieldName]: 'saved' }));
      if (onSave) onSave();
    } catch (error) {
      console.error(`Error saving ${fieldName}:`, error);
      setSavingStates(prev => ({ ...prev, [fieldName]: 'error' }));
    }
  };

  const getSaveIndicator = (fieldName) => {
    const state = savingStates[fieldName];

    const fieldValues = {
      'Grade': grade,
      'GradeComments': gradeComments,
      'AuditOption': auditOption,
      'ReportObjectiveCriteria': reportObjectiveCriteria,
      'TreatmentPathwayID': treatmentPathwayID
    };

    const fieldValue = fieldValues[fieldName];
    const hasValue = fieldValue !== null && fieldValue !== undefined &&
      (Array.isArray(fieldValue) ? fieldValue.length > 0 : fieldValue !== '');

    if (state === 'saving' && hasValue) {
      return (
        <span className="save-status saving" aria-label="Saving">
          <span className="spinner-small"></span>
        </span>
      );
    }
    if (state === 'saved' && hasValue) {
      return (
        <span className="save-status saved" aria-label="Saved">
          <span className="checkmark">✓</span>
        </span>
      );
    }
    if (state === 'error') {
      return (
        <span className="save-status error" aria-label="Error saving">
          <span className="error-mark">⚠</span>
        </span>
      );
    }
    return <span className="save-status" aria-hidden="true"></span>;
  };

  const handleFieldChange = (fieldName, value, setter) => {
    setter(value);

    if (initialCharCounts[fieldName] === undefined) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
    }

    const charsSinceStart = Math.abs(value.length - (initialCharCounts[fieldName] || 0));

    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    if (charsSinceStart >= 50) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
      return;
    }

    const timeoutId = setTimeout(() => {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
    }, 10000);

    setSaveTimeouts(prev => ({ ...prev, [fieldName]: timeoutId }));
  };

  const handleFieldBlur = (fieldName, value) => {
    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
    saveField(fieldName, value);
  };

  const handleSelectChange = (fieldName, value, setter) => {
    setter(value);
    saveField(fieldName, value);
  };

  const handleCheckboxListChange = (fieldName, value) => {
    setReportObjectiveCriteria(prev => {
      const newValue = prev.includes(value)
        ? prev.filter(v => v !== value)
        : [...prev, value];
      saveField(fieldName, newValue);
      return newValue;
    });
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading clinical check...</p>
      </div>
    );
  }

  return (
    <div className="assessment-container">
      <fieldset disabled={isPublished} style={{ border: "none", padding: 0, margin: 0 }}>
      <div className="form-section">
        <h3>Clinical Check</h3>

        <div className="form-group">
          <label htmlFor="grade">
            Grade:
            {getSaveIndicator('Grade')}
          </label>
          <select
            id="grade"
            value={grade}
            onChange={(e) => handleSelectChange('Grade', e.target.value, setGrade)}
            style={{ width: '100%', maxWidth: '300px', padding: '8px' }}
          >
            <option value="">Select</option>
            {GRADE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="gradeComments">
            Clinic communication:
            {getSaveIndicator('GradeComments')}
          </label>
          <textarea
            id="gradeComments"
            value={gradeComments}
            onChange={(e) => handleFieldChange('GradeComments', e.target.value, setGradeComments)}
            onBlur={() => handleFieldBlur('GradeComments', gradeComments)}
            rows="4"
            style={{ width: '100%' }}
            placeholder="Enter clinic communication notes"
          />
        </div>

        {isTriageFollowUp && (
          <div className="form-group">
            <label>
              Objective Criteria:
              {getSaveIndicator('ReportObjectiveCriteria')}
            </label>
            <div className="checkbox-group" style={{ marginTop: '8px' }}>
              {objectiveCriteriaOptions.map(option => (
                <label key={option.value} style={{ display: 'block', marginBottom: '8px' }}>
                  <input
                    type="checkbox"
                    checked={reportObjectiveCriteria.includes(option.value)}
                    onChange={() => handleCheckboxListChange('ReportObjectiveCriteria', option.value)}
                    style={{ marginRight: '8px' }}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>
        )}

        {isEformFollowUp && (
          <div className="form-group">
            <label htmlFor="treatmentPathwayID">
              Please confirm treatment pathway:
              {getSaveIndicator('TreatmentPathwayID')}
            </label>
            <select
              id="treatmentPathwayID"
              value={treatmentPathwayID}
              onChange={(e) => handleSelectChange('TreatmentPathwayID', e.target.value, setTreatmentPathwayID)}
              style={{ width: '100%', maxWidth: '500px', padding: '8px' }}
            >
              <option value="">Select</option>
              {treatmentPathwayOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="auditOption">
            Next Action:
            {getSaveIndicator('AuditOption')}
          </label>
          <select
            id="auditOption"
            value={auditOption}
            onChange={(e) => handleSelectChange('AuditOption', e.target.value, setAuditOption)}
            style={{ width: '100%', maxWidth: '500px', padding: '8px' }}
          >
            <option value="">Select</option>
            {auditOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      </fieldset>

    </div>
  );
};

export default ClinicalCheckTab;
